// src/pages/AdminPanel/UserAccess.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Folder,
  FolderOpen,
} from "lucide-react";
import { supabase } from "services/supabaseClient";
import { useProfile } from "context/ProfileContext";
import { useDialog } from "context/DialogContext";
import SectionCard from "components/SectionCard";
import { logAudit } from "lib/audit";
import { callFunction } from "lib/edge";

function normStr(x) {
  return String(x ?? "").trim();
}

function asLower(x) {
  return normStr(x).toLowerCase();
}

function normEmail(email) {
  return String(email || "").trim().toLowerCase();
}

function normEmailDomain(email) {
  const s = normEmail(email);
  const at = s.lastIndexOf("@");
  if (at < 0) return "";
  return s.slice(at + 1).trim();
}

function getUserType(user) {
  if (user?.is_admin) return "platform_admin";
  if (user?.is_org_admin) return "org_admin";
  return "user";
}

function getUserTypeLabel(value) {
  if (value === "platform_admin") return "Platform Admin";
  if (value === "org_admin") return "Admin";
  return "User";
}

function getUserTypeChanges(value) {
  if (value === "platform_admin") {
    return { is_admin: true, is_org_admin: true };
  }

  if (value === "org_admin") {
    return { is_admin: false, is_org_admin: true };
  }

  return { is_admin: false, is_org_admin: false };
}

function appIsAssignable(app) {
  return !["market-map", "record-room", "recordroom"].includes(app?.slug);
}

function compareValues(aVal, bVal, asc = true) {
  if (aVal == null && bVal == null) return 0;
  if (aVal == null) return asc ? 1 : -1;
  if (bVal == null) return asc ? -1 : 1;

  if (typeof aVal === "number" && typeof bVal === "number") {
    if (aVal < bVal) return asc ? -1 : 1;
    if (aVal > bVal) return asc ? 1 : -1;
    return 0;
  }

  const as = asLower(aVal);
  const bs = asLower(bVal);

  if (as < bs) return asc ? -1 : 1;
  if (as > bs) return asc ? 1 : -1;
  return 0;
}

function userLabel(u) {
  const ln = normStr(u?.last_name);
  const fn = normStr(u?.first_name);
  const name = [ln, fn].filter(Boolean).join(", ") || "(no name)";
  return u?.email ? `${name} — ${u.email}` : name;
}

export default function UserAccess() {
  const {
    profile,
    loading: profileLoading,
    selectedOrg,
  } = useProfile();

  const { alert } = useDialog();

  const isPlatformAdmin = !!profile?.is_admin;
  const isOrgAdmin = !!profile?.is_org_admin;
  const currentUserId = profile?.id;

  const [users, setUsers] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [apps, setApps] = useState([]);
  const [accessMap, setAccessMap] = useState({});
  const [featureAccessMap, setFeatureAccessMap] = useState({});
  const [loading, setLoading] = useState(true);

  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteLoading, setInviteLoading] = useState(false);
  const [temporaryCredentials, setTemporaryCredentials] = useState(null);

  const [linkUserId, setLinkUserId] = useState("");
  const [linkOrgId, setLinkOrgId] = useState("");
  const [grantLoading, setGrantLoading] = useState(false);

  const [updatingOrgKey, setUpdatingOrgKey] = useState("");
  const [updatingRoleUserId, setUpdatingRoleUserId] = useState(null);
  const [updatingAppKey, setUpdatingAppKey] = useState("");
  const [updatingMfaKey, setUpdatingMfaKey] = useState("");
  const [updatingFeatureKey, setUpdatingFeatureKey] = useState("");

  const [openAppsRowKey, setOpenAppsRowKey] = useState(null);
  const [appsMenuDirection, setAppsMenuDirection] = useState("down");

  const [sortKey, setSortKey] = useState("name");
  const [sortAsc, setSortAsc] = useState(true);
  const [collapsedGroups, setCollapsedGroups] = useState(() => new Set());
  const initializedCollapsedGroups = useRef(false);

  const [orgDomain, setOrgDomain] = useState("");
  const [orgDomainLoading, setOrgDomainLoading] = useState(false);

  const inviteAllowedDomain = useMemo(() => {
    return (orgDomain || normEmailDomain(profile?.email)).toLowerCase();
  }, [orgDomain, profile?.email]);

  const inviteDomainMismatch = useMemo(() => {
    if (!isOrgAdmin || isPlatformAdmin) return false;
    if (!inviteEmail) return false;

    const allowed = inviteAllowedDomain;
    if (!allowed) return false;

    const proposed = normEmailDomain(inviteEmail);
    if (!proposed) return false;

    return proposed !== allowed;
  }, [inviteAllowedDomain, inviteEmail, isOrgAdmin, isPlatformAdmin]);

  const assignableOrgs = useMemo(() => {
    if (isPlatformAdmin) return orgs;

    if (selectedOrg?.id) {
      return orgs.filter((o) => o.id === selectedOrg.id);
    }

    return [];
  }, [isPlatformAdmin, orgs, selectedOrg?.id]);

  const selectedGrantUser = useMemo(() => {
    return users.find((u) => u.id === linkUserId) || null;
  }, [linkUserId, users]);

  const selectedGrantUserHasOrg = useMemo(() => {
    if (!selectedGrantUser || !linkOrgId) return false;

    return (selectedGrantUser.org_links || []).some(
      (link) => link.org_id === linkOrgId
    );
  }, [selectedGrantUser, linkOrgId]);

  const canManageOrgLink = (orgId) => {
    if (isPlatformAdmin) return true;
    if (!isOrgAdmin) return false;

    return selectedOrg?.id === orgId;
  };

  const getAssignedApps = (user) => {
    if (!user) return [];
    if (user.is_admin) return [];

    return apps.filter((app) => accessMap?.[user.id]?.[app.id]);
  };

  const columns = useMemo(() => {
    const base = [
      {
        key: "name",
        header: "Name",
        sortValue: (u) =>
          `${normStr(u.last_name)} ${normStr(u.first_name)} ${normStr(
            u.email
          )}`,
        className: "w-[24%]",
      },
      {
        key: "email",
        header: "Email",
        sortValue: (u) => u.email,
        className: "w-[28%]",
      },
      {
        key: "user_type",
        header: "User Type",
        sortValue: (u) => getUserTypeLabel(getUserType(u)),
        className: "w-[16%]",
      },
      {
        key: "org_access",
        header: "Org Access",
        sortValue: () => "",
        className: "w-[12%]",
      },
      {
        key: "mfa",
        header: "MFA",
        sortValue: (u) => (u.display_mfa_required ? 1 : 0),
        className: "w-[10%]",
      },
    ];

    if (isPlatformAdmin) {
      base.push({
        key: "features",
        header: "Features",
        sortValue: () => "",
        className: "w-[12%]",
      });

      base.push({
        key: "apps",
        header: "App Access",
        sortValue: (u) => getAssignedApps(u).length,
        className: "w-[10%]",
      });
    }

    return base;
  }, [isPlatformAdmin, apps, accessMap]);

  const fetchOrgs = async () => {
    if (!isPlatformAdmin && isOrgAdmin && selectedOrg?.id) {
      setOrgs([
        {
          id: selectedOrg.id,
          name: selectedOrg.name || "Selected Organization",
        },
      ]);

      return;
    }

    const { data, error } = await supabase
      .from("orgs")
      .select("id, name")
      .order("name");

    if (error) throw error;

    setOrgs(data || []);
  };

  const fetchUsers = async () => {
    const { data: profilesData, error: profilesError } = await supabase
      .from("profiles")
      .select(
        "id, email, first_name, last_name, is_admin, is_org_admin"
      )
      .order("last_name");

    if (profilesError) throw profilesError;

    const userIds = (profilesData || []).map((u) => u.id);

    let linkRows = [];

    if (userIds.length > 0) {
      const today = new Date().toISOString().slice(0, 10);

      const { data: linksData, error: linksError } = await supabase
        .from("org_user_links")
        .select(
          `
            user_id,
            org_id,
            role,
            effective_from,
            effective_to,
            mfa_required,
            mfa_required_at,
            mfa_required_by,
            orgs (
              id,
              name
            )
          `
        )
        .in("user_id", userIds)
        .lte("effective_from", today)
        .or(`effective_to.is.null,effective_to.gte.${today}`);

      if (linksError) throw linksError;

      linkRows = linksData || [];
    }

    const linksByUserId = {};

    for (const link of linkRows) {
      if (!linksByUserId[link.user_id]) {
        linksByUserId[link.user_id] = [];
      }

      linksByUserId[link.user_id].push({
        org_id: link.org_id,
        org_name: link.orgs?.name || "Organization",
        org_role: link.role || "",
        effective_from: link.effective_from,
        mfa_required: !!link.mfa_required,
        mfa_required_at: link.mfa_required_at || null,
        mfa_required_by: link.mfa_required_by || null,
      });
    }

    setUsers(
      (profilesData || []).map((u) => ({
        ...u,
        org_links: linksByUserId[u.id] || [],
      }))
    );
  };

  const fetchAppsAndAccess = async () => {
    if (!isPlatformAdmin) return;

    const { data: appsData, error: appsErr } = await supabase
      .from("apps")
      .select("id, name, slug")
      .order("name");

    if (appsErr) throw appsErr;

    const filteredApps = (appsData || []).filter(appIsAssignable);

    const { data: accessData, error: accessErr } = await supabase
      .from("user_app_access")
      .select("user_id, app_id");

    if (accessErr) throw accessErr;

    const map = {};

    for (const { user_id, app_id } of accessData || []) {
      if (!map[user_id]) map[user_id] = {};
      map[user_id][app_id] = true;
    }

    setApps(filteredApps);
    setAccessMap(map);
  };

  const fetchFeatureAccess = async () => {
    if (!isPlatformAdmin) {
      setFeatureAccessMap({});
      return;
    }

    const query = supabase
      .from("org_user_feature_access")
      .select("user_id, org_id, feature_key");

    const { data, error } = await query;
    if (error) throw error;

    const map = {};
    for (const row of data || []) {
      map[`${row.user_id}:${row.org_id}:${row.feature_key}`] = true;
    }
    setFeatureAccessMap(map);
  };

  const fetchOrgDomain = async () => {
    if (!isOrgAdmin || isPlatformAdmin) return;

    setOrgDomainLoading(true);

    try {
      setOrgDomain(normEmailDomain(profile?.email));
    } finally {
      setOrgDomainLoading(false);
    }
  };

  useEffect(() => {
    if (profileLoading) return;

    setLoading(true);

    Promise.all([
      fetchUsers(),
      fetchOrgs(),
      fetchAppsAndAccess(),
      fetchFeatureAccess(),
      fetchOrgDomain(),
    ])
      .catch((err) => alert(err.message))
      .finally(() => setLoading(false));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileLoading, profile?.id, selectedOrg?.id]);

  useEffect(() => {
    if (!isPlatformAdmin && isOrgAdmin && selectedOrg?.id) {
      setLinkOrgId(selectedOrg.id);
    }
  }, [isPlatformAdmin, isOrgAdmin, selectedOrg?.id]);

  const handleSortClick = (key) => {
    if (sortKey === key) {
      setSortAsc((v) => !v);
      return;
    }

    setSortKey(key);
    setSortAsc(true);
  };

  const renderSortIcon = (key) => {
    if (sortKey !== key) {
      return <ArrowUpDown className="h-3.5 w-3.5" />;
    }

    return sortAsc ? (
      <ArrowUp className="h-3.5 w-3.5 text-app" />
    ) : (
      <ArrowDown className="h-3.5 w-3.5 text-app" />
    );
  };

  const sortedUsers = useMemo(() => {
    const col = columns.find((c) => c.key === sortKey);
    const list = [...users];

    if (!col || typeof col.sortValue !== "function") {
      return list;
    }

    list.sort((a, b) =>
      compareValues(col.sortValue(a), col.sortValue(b), sortAsc)
    );

    return list;
  }, [columns, sortKey, sortAsc, users]);

  const groupedUsers = useMemo(() => {
    const map = new Map();

    for (const user of sortedUsers) {
      const orgLinks = Array.isArray(user.org_links)
        ? user.org_links
        : [];

      if (orgLinks.length === 0) {
        const groupId = "__no_org__";

        if (!map.has(groupId)) {
          map.set(groupId, {
            id: groupId,
            label: "No Organization",
            rows: [],
          });
        }

        map.get(groupId).rows.push(user);
        continue;
      }

      for (const link of orgLinks) {
        const groupId = link.org_id || "__no_org__";

        if (!map.has(groupId)) {
          map.set(groupId, {
            id: groupId,
            label: link.org_name || "Organization",
            rows: [],
          });
        }

        map.get(groupId).rows.push({
          ...user,
          display_org_id: link.org_id,
          display_org_name: link.org_name,
          display_org_role: link.org_role,
          display_effective_from: link.effective_from,
          display_mfa_required: !!link.mfa_required,
          display_mfa_required_at: link.mfa_required_at,
          display_mfa_required_by: link.mfa_required_by,
        });
      }
    }

    const groups = Array.from(map.values());

    groups.sort((a, b) =>
      compareValues(a.label, b.label, true)
    );

    return groups;
  }, [sortedUsers]);

  useEffect(() => {
    if (initializedCollapsedGroups.current || !groupedUsers.length) return;
    setCollapsedGroups(new Set(groupedUsers.map((group) => group.id)));
    initializedCollapsedGroups.current = true;
  }, [groupedUsers]);

  const allCollapsed = useMemo(() => {
    if (!groupedUsers.length) return false;

    return groupedUsers.every((g) =>
      collapsedGroups.has(g.id)
    );
  }, [collapsedGroups, groupedUsers]);

  const toggleGroup = (groupId) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);

      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);

      return next;
    });
  };

  const collapseAll = () => {
    setCollapsedGroups(
      new Set(groupedUsers.map((g) => g.id))
    );
  };

  const expandAll = () => {
    setCollapsedGroups(new Set());
  };

  const handleInvite = async () => {
    const email = normEmail(inviteEmail);

    if (!email) {
      await alert("Enter an email address.");
      return;
    }

    if (inviteDomainMismatch) {
      await logAudit({
        category: "user",
        action: "invite_blocked_domain_mismatch",
        success: false,
        detail: {
          email,
          allowed_domain: inviteAllowedDomain || null,
          proposed_domain: normEmailDomain(email) || null,
        },
      });

      await alert(
        `Org admins can only invite users with an email at ${inviteAllowedDomain}.`
      );

      return;
    }

    const redirectTo = `${window.location.origin}/activate`;

    setInviteLoading(true);

    try {
      const result = await callFunction("invite-user", {
        body: {
          email,
          org_id: isOrgAdmin ? selectedOrg?.id : undefined,
          allowed_domain:
            isOrgAdmin && !isPlatformAdmin
              ? inviteAllowedDomain
              : undefined,
          redirect_to: redirectTo,
        },
      });

      setInviteEmail("");
      await fetchUsers();
      await alert(
        result?.invitation_status === "recovery_sent"
          ? "The existing incomplete account was sent a secure recovery code."
          : "Invitation code sent.",
        { title: "Invitation sent" }
      );
    } catch (err) {
      await alert(err.message || "The invitation could not be sent.", {
        title: "Invitation unavailable",
      });
    } finally {
      setInviteLoading(false);
    }
  };

  const handleCreateWithTemporaryPassword = async () => {
    const email = normEmail(inviteEmail);

    if (!email) {
      await alert("Enter an email address.");
      return;
    }

    setInviteLoading(true);
    setTemporaryCredentials(null);

    try {
      const result = await callFunction("invite-user", {
        body: {
          action: "create_with_temporary_password",
          email,
          org_id: selectedOrg?.id || undefined,
        },
      });

      await logAudit({
        category: "user",
        action: "user_created_with_temporary_password",
        success: true,
        detail: {
          email,
          org_id: selectedOrg?.id || null,
          user_id: result?.user_id || null,
        },
      });

      setTemporaryCredentials({
        email: result.email,
        password: result.temporary_password,
      });
      setInviteEmail("");
      await fetchUsers();
    } catch (err) {
      await alert(err.message || "The user could not be established.", {
        title: "User creation unavailable",
      });
    } finally {
      setInviteLoading(false);
    }
  };

  const copyTemporaryCredentials = async () => {
    if (!temporaryCredentials) return;

    const text = `CoreCompass username: ${temporaryCredentials.email}\nTemporary password: ${temporaryCredentials.password}`;
    try {
      await navigator.clipboard.writeText(text);
      await alert("The temporary credentials were copied to the clipboard.", {
        title: "Credentials copied",
      });
    } catch {
      await alert("Clipboard access was unavailable. Copy the displayed credentials manually.", {
        title: "Copy unavailable",
      });
    }
  };

  const grantOrgAccess = async (e) => {
    e.preventDefault();

    if (!linkUserId || !linkOrgId) {
      await alert("Select a user and organization.");
      return;
    }

    if (!canManageOrgLink(linkOrgId)) {
      await alert(
        "You do not have permission to grant access to this organization."
      );

      return;
    }

    if (selectedGrantUserHasOrg) {
      await alert(
        "This user already has active access to this organization."
      );

      return;
    }

    setGrantLoading(true);

    try {
      const effectiveFrom = new Date()
        .toISOString()
        .slice(0, 10);

      const { error } = await supabase
        .from("org_user_links")
        .insert({
          user_id: linkUserId,
          org_id: linkOrgId,
          effective_from: effectiveFrom,
        });

      if (error) throw error;

      await logAudit({
        category: "org",
        action: "org_access_granted",
        success: true,
        target_user_id: linkUserId,
        detail: {
          org_id: linkOrgId,
          effective_from: effectiveFrom,
        },
      });

      setLinkUserId("");

      if (isPlatformAdmin) {
        setLinkOrgId("");
      }

      await fetchUsers();
    } catch (err) {
      await alert(err.message || String(err));
    } finally {
      setGrantLoading(false);
    }
  };

  const revokeOrgAccess = async (
    userId,
    orgId,
    effectiveFrom
  ) => {
    if (!canManageOrgLink(orgId)) {
      await alert(
        "You do not have permission to revoke access to this organization."
      );

      return;
    }

    const orgKey = `${userId}:${orgId}:${effectiveFrom}`;

    setUpdatingOrgKey(orgKey);

    try {
      const { error } = await supabase
        .from("org_user_links")
        .delete()
        .eq("user_id", userId)
        .eq("org_id", orgId)
        .eq("effective_from", effectiveFrom);

      if (error) throw error;

      await logAudit({
        category: "org",
        action: "org_access_revoked",
        success: true,
        target_user_id: userId,
        detail: {
          org_id: orgId,
          effective_from: effectiveFrom,
        },
      });

      await fetchUsers();
    } catch (err) {
      await alert(err.message || String(err));
    } finally {
      setUpdatingOrgKey("");
    }
  };

  const updateMfaRequired = async (
    userId,
    orgId,
    effectiveFrom,
    nextRequired
  ) => {
    if (!canManageOrgLink(orgId)) {
      await alert(
        "You do not have permission to update MFA policy for this organization."
      );

      return;
    }

    const mfaKey = `${userId}:${orgId}:${effectiveFrom}`;
    setUpdatingMfaKey(mfaKey);

    try {
      const update = nextRequired
        ? {
            mfa_required: true,
            mfa_required_at: new Date().toISOString(),
            mfa_required_by: currentUserId,
          }
        : {
            mfa_required: false,
            mfa_required_at: null,
            mfa_required_by: null,
          };

      const { error } = await supabase
        .from("org_user_links")
        .update(update)
        .eq("user_id", userId)
        .eq("org_id", orgId)
        .eq("effective_from", effectiveFrom);

      if (error) throw error;

      await logAudit({
        category: "org",
        action: nextRequired ? "org_user_mfa_required" : "org_user_mfa_optional",
        success: true,
        target_user_id: userId,
        detail: {
          org_id: orgId,
          effective_from: effectiveFrom,
          mfa_required: nextRequired,
        },
      });

      await fetchUsers();
    } catch (err) {
      await alert(err.message || String(err));
    } finally {
      setUpdatingMfaKey("");
    }
  };

  const updateUserType = async (u, nextUserType) => {
    if (u.id === currentUserId) return;

    const currentUserType = getUserType(u);

    if (currentUserType === nextUserType) return;

    if (
      !isPlatformAdmin &&
      nextUserType === "platform_admin"
    ) {
      await alert(
        "Only platform admins can grant platform admin access."
      );

      return;
    }

    setUpdatingRoleUserId(u.id);

    try {
      await callFunction("set-user-roles", {
        body: {
          user_id: u.id,
          ...getUserTypeChanges(nextUserType),
        },
      });

      await logAudit({
        category: "user",
        action: "user_type_updated",
        success: true,
        target_user_id: u.id,
        detail: {
          previous_user_type: currentUserType,
          next_user_type: nextUserType,
        },
      });

      await fetchUsers();
    } catch (err) {
      await alert(err.message || String(err));
    } finally {
      setUpdatingRoleUserId(null);
    }
  };

  const toggleAppAccess = async (userId, appId) => {
    if (!isPlatformAdmin) return;

    const user = users.find((u) => u.id === userId);

    if (user?.is_admin) return;

    const hasAccess = accessMap?.[userId]?.[appId];
    const appKey = `${userId}:${appId}`;

    setUpdatingAppKey(appKey);

    let error;

    if (hasAccess) {
      ({ error } = await supabase
        .from("user_app_access")
        .delete()
        .eq("user_id", userId)
        .eq("app_id", appId));
    } else {
      ({ error } = await supabase
        .from("user_app_access")
        .insert({
          user_id: userId,
          app_id: appId,
        }));
    }

    setUpdatingAppKey("");

    if (error) {
      await alert(error.message);
      return;
    }

    await fetchAppsAndAccess();
  };

  const toggleFeatureAccess = async (userId, orgId) => {
    if (!isPlatformAdmin || !orgId || !canManageOrgLink(orgId)) return;
    const featureKey = "fund-planner.smartdoc-import";
    const mapKey = `${userId}:${orgId}:${featureKey}`;
    const enabled = !!featureAccessMap[mapKey];
    setUpdatingFeatureKey(mapKey);
    const { error } = enabled
      ? await supabase.from("org_user_feature_access").delete().eq("user_id", userId).eq("org_id", orgId).eq("feature_key", featureKey)
      : await supabase.from("org_user_feature_access").insert({ user_id: userId, org_id: orgId, feature_key: featureKey, granted_by: currentUserId });
    setUpdatingFeatureKey("");
    if (error) { await alert(error.message); return; }
    await logAudit({ category: "user", action: enabled ? "smartdoc_access_disabled" : "smartdoc_access_enabled", success: true, target_user_id: userId, detail: { org_id: orgId, feature_key: featureKey } });
    await fetchFeatureAccess();
  };

  if (profileLoading || loading) {
    return <div className="text-app">Loading users…</div>;
  }

  if (!isPlatformAdmin && !isOrgAdmin) {
    return <div className="text-app">Admin access required.</div>;
  }

  return (
    <div className="space-y-6">
      <div className={isPlatformAdmin ? "grid gap-6 lg:grid-cols-2" : "grid gap-6"}>
        <SectionCard>
          <div className="mb-3 text-lg font-semibold text-app">
            Invite User
          </div>

          {isOrgAdmin && !isPlatformAdmin && (
            <div className="mb-3 text-sm text-muted">
              {orgDomainLoading ? (
                <>Loading organization domain…</>
              ) : inviteAllowedDomain ? (
                <>
                  Org admins can invite users with email at{" "}
                  <span className="font-medium text-app">
                    {inviteAllowedDomain}
                  </span>
                  .
                </>
              ) : (
                <>
                  Org admins can invite users within
                  their organization’s email domain.
                </>
              )}
            </div>
          )}

          <div className="flex flex-wrap gap-3">
            <input
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              className="h-8 w-72 rounded-md border border-input bg-background px-3 text-sm text-foreground"
              placeholder="Enter email"
            />

            <button
              type="button"
              onClick={handleInvite}
              disabled={inviteLoading || inviteDomainMismatch}
              className="btn btn-primary"
            >
              {inviteLoading ? "Sending…" : "Invite"}
            </button>

            {isPlatformAdmin && (
              <button
                type="button"
                onClick={handleCreateWithTemporaryPassword}
                disabled={inviteLoading}
                className="btn btn-secondary"
              >
                {inviteLoading ? "Working…" : "Create With Temporary Password"}
              </button>
            )}
          </div>

          {temporaryCredentials && isPlatformAdmin && (
            <div className="mt-4 rounded-md border border-amber-300 bg-amber-50 p-4 text-sm text-amber-950">
              <div className="font-semibold">Copy these credentials now</div>
              <p className="mt-1">
                This temporary password is displayed only once. The user must replace it at first login.
              </p>
              <dl className="mt-3 grid gap-2">
                <div>
                  <dt className="font-medium">Username</dt>
                  <dd className="break-all font-mono">{temporaryCredentials.email}</dd>
                </div>
                <div>
                  <dt className="font-medium">Temporary password</dt>
                  <dd className="break-all font-mono">{temporaryCredentials.password}</dd>
                </div>
              </dl>
              <div className="mt-3 flex flex-wrap gap-2">
                <button type="button" onClick={copyTemporaryCredentials} className="btn btn-primary">
                  Copy Credentials
                </button>
                <button
                  type="button"
                  onClick={() => setTemporaryCredentials(null)}
                  className="btn btn-ghost"
                >
                  Dismiss
                </button>
              </div>
            </div>
          )}
        </SectionCard>

        {isPlatformAdmin && (
          <SectionCard>
            <div className="mb-3 text-lg font-semibold text-app">
              Grant Organization Access
            </div>

            <form
              onSubmit={grantOrgAccess}
              className="flex flex-col gap-3"
            >
              <div>
                <label className="mb-1 block text-xs font-medium text-muted">
                  User
                </label>

                <select
                  value={linkUserId}
                  onChange={(e) => setLinkUserId(e.target.value)}
                  className="h-8 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground"
                >
                  <option value="">Select user</option>

                  {users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {userLabel(u)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="mb-1 block text-xs font-medium text-muted">
                  Organization
                </label>

                <select
                  value={linkOrgId}
                  onChange={(e) => setLinkOrgId(e.target.value)}
                  className="h-8 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground"
                >
                  <option value="">Select organization</option>

                  {assignableOrgs.map((org) => (
                    <option key={org.id} value={org.id}>
                      {org.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={
                    grantLoading ||
                    !linkUserId ||
                    !linkOrgId ||
                    selectedGrantUserHasOrg
                  }
                >
                  {grantLoading ? "Granting…" : "Grant Access"}
                </button>
              </div>
            </form>
          </SectionCard>
        )}
      </div>

      <SectionCard>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-lg font-semibold text-app">
              Org-User Permissions
            </div>

            <div className="text-sm text-muted">
              Manage user type, organization access and application access.
            </div>
          </div>

          <button
            type="button"
            className="btn btn-ghost h-8 px-3 text-sm"
            onClick={
              allCollapsed
                ? expandAll
                : collapseAll
            }
            disabled={groupedUsers.length === 0}
          >
            {allCollapsed
              ? "Expand All"
              : "Collapse All"}
          </button>
        </div>

        <div className="relative overflow-visible rounded-xl border border-app">
          <table className="w-full table-fixed text-sm">
            <thead className="bg-app text-muted shadow-[inset_0_-1px_0_hsl(var(--border))]">
              <tr className="select-none">
                {columns.map((col) => (
                  <th
                    key={col.key}
                    className={[
                      "cursor-pointer px-4 py-2 text-left font-bold",
                      col.className || "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    onClick={() =>
                      handleSortClick(col.key)
                    }
                  >
                    <div className="flex min-w-0 items-center gap-1">
                      <span className="truncate">
                        {col.header}
                      </span>

                      {renderSortIcon(col.key)}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {groupedUsers.map((group) => {
                const collapsed =
                  collapsedGroups.has(group.id);

                const count = group.rows.length;

                return (
                  <React.Fragment
                    key={group.id}
                  >
                    <tr
                      className="select-none"
                      onClick={() =>
                        toggleGroup(group.id)
                      }
                    >
                      <td
                        colSpan={columns.length}
                        className="cursor-pointer bg-white/5 px-4 py-2 hover:bg-white/10"
                      >
                        <div className="flex min-w-0 items-center gap-2">
                          {collapsed ? (
                            <Folder className="h-4 w-4 shrink-0 text-muted" />
                          ) : (
                            <FolderOpen className="h-4 w-4 shrink-0 text-muted" />
                          )}

                          <div className="min-w-0 flex-1 truncate font-semibold text-app">
                            {group.label}
                          </div>

                          <div className="shrink-0 whitespace-nowrap text-xs text-muted">
                            {count}{" "}
                            {count === 1
                              ? "user"
                              : "users"}
                          </div>
                        </div>
                      </td>
                    </tr>

                    {!collapsed &&
                      group.rows.map((u) => {
                        const rowKey = `${group.id}:${u.id}`;

                        const isSelf =
                          u.id === currentUserId;

                        const roleDisabled =
                          isSelf ||
                          updatingRoleUserId ===
                            u.id;

                        const assignedApps =
                          getAssignedApps(u);

                        return (
                          <tr
                            key={rowKey}
                            className="border-t border-app hover:bg-app/50"
                          >
                            <td className="px-4 py-2">
                              <div className="ml-6 min-w-0 font-medium text-app">
                                <span className="truncate">
                                  {u.first_name}{" "}
                                  {u.last_name}
                                </span>

                                {isSelf && (
                                  <span className="text-muted">
                                    {" "}
                                    (you)
                                  </span>
                                )}
                              </div>
                            </td>

                            <td className="px-4 text-app">
                              <div className="truncate">
                                {u.email}
                              </div>
                            </td>

                            <td className="px-4">
                              <select
                                value={getUserType(u)}
                                disabled={
                                  roleDisabled
                                }
                                onChange={(e) =>
                                  updateUserType(
                                    u,
                                    e.target.value
                                  )
                                }
                                className="h-8 rounded-md border border-input bg-background px-2 text-sm text-foreground"
                              >
                                <option value="user">
                                  User
                                </option>

                                <option value="org_admin">
                                  Admin
                                </option>

                                {isPlatformAdmin && (
                                  <option value="platform_admin">
                                    Platform Admin
                                  </option>
                                )}
                              </select>
                            </td>

                            <td className="px-4">
                              {u.display_org_id &&
                              canManageOrgLink(
                                u.display_org_id
                              ) ? (
                                <button
                                  type="button"
                                  className="text-sm text-red-600 hover:underline"
                                  disabled={
                                    updatingOrgKey ===
                                    `${u.id}:${u.display_org_id}:${u.display_effective_from}`
                                  }
                                  onClick={() =>
                                    revokeOrgAccess(
                                      u.id,
                                      u.display_org_id,
                                      u.display_effective_from
                                    )
                                  }
                                >
                                  Revoke
                                </button>
                              ) : (
                                <span className="text-xs text-muted">
                                  —
                                </span>
                              )}
                            </td>

                            <td className="px-4">
                              {u.display_org_id ? (
                                <label
                                  className="inline-flex items-center gap-2 text-sm text-app"
                                  title="Require MFA for this user in this organization"
                                >
                                  <input
                                    type="checkbox"
                                    aria-label="Require MFA"
                                    checked={!!u.display_mfa_required}
                                    disabled={
                                      !canManageOrgLink(u.display_org_id) ||
                                      updatingMfaKey ===
                                        `${u.id}:${u.display_org_id}:${u.display_effective_from}`
                                    }
                                    onChange={(e) =>
                                      updateMfaRequired(
                                        u.id,
                                        u.display_org_id,
                                        u.display_effective_from,
                                        e.target.checked
                                      )
                                    }
                                  />
                                </label>
                              ) : (
                                <span className="text-xs text-muted">—</span>
                              )}
                            </td>

                            {isPlatformAdmin && (
                              <td className="px-4">
                                {u.display_org_id ? (
                                  <label className="inline-flex items-center gap-2 text-xs text-app" title="Allow FundPlanner SmartDoc statement imports for this organization">
                                    <input
                                      type="checkbox"
                                      checked={!!featureAccessMap[`${u.id}:${u.display_org_id}:fund-planner.smartdoc-import`]}
                                      disabled={updatingFeatureKey === `${u.id}:${u.display_org_id}:fund-planner.smartdoc-import`}
                                      onChange={() => toggleFeatureAccess(u.id, u.display_org_id)}
                                    />
                                    SmartDoc
                                  </label>
                                ) : <span className="text-xs text-muted">—</span>}
                              </td>
                            )}

                            {isPlatformAdmin && (
                              <td className="relative px-4">
                                {u.is_admin ? (
                                  <span className="text-muted">
                                    Platform access
                                  </span>
                                ) : (
                                  <div className="relative inline-block text-left">
                                    <button
                                      type="button"
                                      className="btn btn-ghost h-8 px-3 text-sm"
                                      onClick={(event) => {
                                        if (openAppsRowKey === rowKey) {
                                          setOpenAppsRowKey(null);
                                          return;
                                        }

                                        const sectionBounds = event.currentTarget
                                          .closest("section")
                                          ?.getBoundingClientRect();
                                        const buttonBounds =
                                          event.currentTarget.getBoundingClientRect();
                                        const estimatedMenuHeight = Math.min(
                                          320,
                                          58 + apps.length * 44
                                        );
                                        const spaceBelow = sectionBounds
                                          ? sectionBounds.bottom - buttonBounds.bottom
                                          : window.innerHeight - buttonBounds.bottom;
                                        const spaceAbove = sectionBounds
                                          ? buttonBounds.top - sectionBounds.top
                                          : buttonBounds.top;

                                        setAppsMenuDirection(
                                          spaceBelow < estimatedMenuHeight &&
                                            spaceAbove > spaceBelow
                                            ? "up"
                                            : "down"
                                        );
                                        setOpenAppsRowKey(rowKey);
                                      }}
                                    >
                                      {
                                        assignedApps.length
                                      }{" "}
                                      active ▾
                                    </button>

                                    {openAppsRowKey ===
                                      rowKey && (
                                      <div
                                        className={`absolute right-0 z-30 w-72 rounded-xl border border-app bg-app p-3 shadow-lg ${
                                          appsMenuDirection === "up"
                                            ? "bottom-full mb-2"
                                            : "top-full mt-2"
                                        }`}
                                      >
                                        <div className="mb-2 text-sm font-semibold text-app">
                                          Apps
                                        </div>

                                        <div className="max-h-64 space-y-2 overflow-y-auto">
                                          {apps.map(
                                            (
                                              app
                                            ) => {
                                              const hasAccess =
                                                !!accessMap?.[
                                                  u.id
                                                ]?.[
                                                  app.id
                                                ];

                                              const appKey = `${u.id}:${app.id}`;

                                              const isUpdating =
                                                updatingAppKey ===
                                                appKey;

                                              return (
                                                <label
                                                  key={
                                                    app.id
                                                  }
                                                  className="flex cursor-pointer items-center justify-between gap-3 rounded-lg px-2 py-2 hover:bg-app/50"
                                                >
                                                  <div className="flex items-center gap-2">
                                                    <input
                                                      type="checkbox"
                                                      checked={
                                                        hasAccess
                                                      }
                                                      disabled={
                                                        isUpdating
                                                      }
                                                      onChange={() =>
                                                        toggleAppAccess(
                                                          u.id,
                                                          app.id
                                                        )
                                                      }
                                                    />

                                                    <span className="text-sm text-app">
                                                      {
                                                        app.name
                                                      }
                                                    </span>
                                                  </div>

                                                  <span className="text-xs text-muted">
                                                    {hasAccess
                                                      ? "Active"
                                                      : "Inactive"}
                                                  </span>
                                                </label>
                                              );
                                            }
                                          )}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </td>
                            )}
                          </tr>
                        );
                      })}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </SectionCard>
    </div>
  );
}
